// AVRASTRO Video in the CRM (requirements 01–03). The CRM never carries media: the call runs on the platform's meeting page
// (own window, or embedded in this side panel) — so nothing of the CRM is ever shared by itself. From here the advisor:
//   - creates the appointment's room (only with the `video` add-on: the platform decides, this screen only asks),
//   - sends the guest link (message drafts, calendar file — the existing appointment flow),
//   - lets people in from the waiting room, denies, locks, ends for everyone,
//   - shows the client a «Shared View» of a Meeting Mode step: a client-safe payload built from an allow-list
//     (@vero/domain sharedMeetingView — no commission, no private notes, no other clients), relayed by the platform,
//   - posts an e-sign link or the client-portal link in the call chat,
//   - keeps notes and follow-ups on the client's meeting record (meetings.taskId = the appointment).
// Without the add-on, or without a LiveKit server, appointments work exactly as before (the advisor's own video link).
import { useEffect, useRef, useState } from 'react';
import { addDays, completeMeeting, linkVideoRoom, saveMeeting, sharedMeetingView, videoOfTask, SHARED_STEPS, type SharedStep, type SharedView, type Task } from '@vero/domain';
import { PlatformError, type VideoHostCard, type VideoRoom, type VideoStatus } from '@vero/platform';
import { useRuntime, type Runtime } from '../core/runtime.tsx';
import { Field } from '../core/ui.tsx';
import { Icon } from '../core/icons.tsx';
import { DateField } from '../core/date-field.tsx';
import { accountService } from '../core/account-service.ts';
import { useAccount } from './account.tsx';
import { SendForSignature } from './esign.tsx';
import { openExternal } from '../core/links.ts';

const isDesktopApp = () => typeof globalThis !== 'undefined' && '__TAURI_INTERNALS__' in globalThis;
/** a browser (or a pane inside another app, e.g. the Claude desktop browser) that cannot use the camera: the call window
 *  would open without camera or microphone — the panel offers the link for Safari / Chrome instead */
async function cameraBlocked(): Promise<boolean> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) return true;
  try { const st = await navigator.permissions?.query({ name: 'camera' as PermissionName }); return st?.state === 'denied'; } catch { return false; }
}
/** the advisor's business card on the meeting page (branding profile → platform); sent when it changes */
let cardSent = '';
function hostCard(rt: Runtime): VideoHostCard {
  const b = rt.brand;
  return { name: b.advisorName, title: b.jobTitle, agency: b.agencyName, photo: b.photo, phone: b.phone, email: b.email, whatsapp: b.phone, website: b.website ? (/^https?:/i.test(b.website) ? b.website.replace(/^http:/i, 'https:') : `https://${b.website}`) : null };
}

type Ev = { target: HTMLInputElement };
let cache: { at: number; value: VideoStatus } | null = null;

/** what the platform says about video for this workspace (null: signed out / no platform) */
export function useVideoStatus(): VideoStatus | null {
  const a = useAccount();
  const [s, setS] = useState<VideoStatus | null>(cache?.value ?? null);
  useEffect(() => {
    if (!a?.signedIn) { setS(null); return; }
    if (cache && Date.now() - cache.at < 60_000) { setS(cache.value); return; }
    let live = true;
    void accountService()?.api.videoStatus().then((v) => { cache = { at: Date.now(), value: v }; if (live) setS(v); }).catch(() => { if (live) setS(null); });
    return () => { live = false; };
  }, [a?.signedIn]);
  return s;
}
export const videoErr = (rt: Runtime, e: unknown) => {
  const code = e instanceof PlatformError ? e.message : 'network';
  const k = `vcall.err.${code}`;
  const v = rt.t(k);
  return v === k ? rt.t('vcall.err.generic', { code }) : v;
};
const localIso = (date: string, time: string) => new Date(`${date}T${time || '09:00'}:00`).toISOString();

/** after an appointment is saved: its AVRASTRO Video room (and the link on the appointment) */
export async function createVideoRoom(rt: Runtime, task: Task): Promise<VideoRoom | null> {
  const api = accountService()?.api;
  if (!api || !task.dueDate) return null;
  try {
    const room = await api.videoCreate({ title: task.title, startsAt: localIso(task.dueDate, task.dueTime ?? '09:00'), durationMin: task.durationMin ?? 45, taskId: task.id, personId: task.personId ?? null, lang: rt.locale === 'en' ? 'en' : 'el' });
    await rt.run((v, c) => linkVideoRoom(v, task.id, { roomId: room.id, mode: room.mode }, c, rt.t('vcall.location')));
    return room;
  } catch (e) { rt.toast(videoErr(rt, e), 'error'); return null; }
}

/** after an appointment's date, time, length or title changed: its room follows (same guest link, new opening time) */
export async function moveVideoRoom(rt: Runtime, task: Task): Promise<boolean> {
  const api = accountService()?.api;
  const v = videoOfTask(task);
  if (!api || !v || !task.dueDate) return false;
  try {
    await api.videoUpdate(v.roomId, { title: task.title, startsAt: localIso(task.dueDate, task.dueTime ?? '09:00'), durationMin: task.durationMin ?? 45 });
    return true;
  } catch (e) { rt.toast(videoErr(rt, e), 'error'); return false; }
}

/** the appointment form's choice: in person or video (+ an AVRASTRO room when the add-on is active) */
export function AppointmentMode({ mode, onMode, room, onRoom }: { mode: 'physical' | 'video'; onMode: (m: 'physical' | 'video') => void; room: boolean; onRoom: (b: boolean) => void }) {
  const rt = useRuntime();
  const st = useVideoStatus();
  return (
    <Field label={rt.t('vcall.mode')} span2 group>
      <div className="segmented" role="group" data-testid="appt-mode">
        {(['physical', 'video'] as const).map((m) => <button key={m} type="button" aria-pressed={mode === m} onClick={() => onMode(m)} data-testid={`appt-mode-${m}`}>{rt.t(`vcall.mode_${m}`)}</button>)}
      </div>
      {mode === 'video' && (st?.configured
        ? st.entitled
          ? <label className="check small" style={{ marginTop: 6 }}><input type="checkbox" checked={room} onChange={(e: Ev) => onRoom(e.target.checked)} data-testid="appt-video-room" />{rt.t('vcall.create_room')}</label>
          : <small className="muted small" data-testid="appt-video-addon">{rt.t('vcall.addon_needed')}</small>
        : <small className="muted small">{rt.t('vcall.own_link')}</small>)}
    </Field>
  );
}

/** the guest link of an appointment's room, for the confirmation step (copy + into the message drafts) */
export function useTaskRoom(task: Task | undefined): [VideoRoom | null, () => void] {
  const [room, setRoom] = useState<VideoRoom | null>(null);
  const v = videoOfTask(task);
  const load = () => { if (v) void accountService()?.api.videoRoom(v.roomId).then(setRoom).catch(() => setRoom(null)); };
  useEffect(load, [v?.roomId]);
  return [room, load];
}

export function VideoLinkBox({ room }: { room: VideoRoom }) {
  const rt = useRuntime();
  return (
    <div className="callout small" data-testid="video-link-box">
      <b>{rt.t('vcall.guest_link')}</b>
      <div className="row" style={{ gap: 8, marginTop: 6 }}><input className="input grow" readOnly value={room.guestUrl} data-testid="video-guest-url" /><button className="btn btn-sm" onClick={() => void navigator.clipboard?.writeText(room.guestUrl).then(() => rt.toast(rt.t('common.copied'), 'success'))}><Icon name="copy" />{rt.t('common.copy')}</button></div>
      <div className="tiny muted" style={{ marginTop: 4 }}>{rt.t('vcall.link_note', { from: rt.i18n.time(new Date(room.opensAt).toTimeString().slice(0, 5)) })}</div>
      {room.keyUrl && <div style={{ marginTop: 8 }} data-testid="video-key-box">
        <b>{rt.t('vcall.key_link')}</b>
        <div className="row" style={{ gap: 8, marginTop: 6 }}><input className="input grow" readOnly value={room.keyUrl} data-testid="video-key-url" /><button className="btn btn-sm" onClick={() => void navigator.clipboard?.writeText(room.keyUrl!).then(() => rt.toast(rt.t('common.copied'), 'success'))}><Icon name="copy" />{rt.t('common.copy')}</button></div>
        <div className="tiny muted" style={{ marginTop: 4 }}>{rt.t('vcall.key_note')}</div>
      </div>}
    </div>
  );
}

/** the button on an agenda item / appointment that opens the side panel */
export function VideoButton({ task, small }: { task: Task; small?: boolean }) {
  const rt = useRuntime();
  if (!videoOfTask(task)) return null;
  return <button className={`btn ${small ? 'btn-sm btn-ghost' : ''}`} onClick={(e: { stopPropagation(): void }) => { e.stopPropagation(); rt.close(); openVideoPanel(rt, task.id); }} aria-label={rt.t('vcall.open_panel')} data-testid="video-open-panel"><Icon name="camera" />{small ? '' : rt.t('vcall.open_panel')}</button>;
}
export function openVideoPanel(rt: Runtime, taskId: string) { rt.openLayer(<VideoPanel key={taskId} taskId={taskId} />); }

/** the side panel: lobby, link, Shared View, chat links, notes and follow-ups — the call itself in its own window */
function VideoPanel({ taskId }: { taskId: string }) {
  const rt = useRuntime();
  const { t, view, today } = rt;
  const task = view.get('tasks', taskId);
  const p = task?.personId ? view.get('persons', task.personId) : undefined;
  const v = videoOfTask(task);
  const api = accountService()?.api;
  const [room, setRoom] = useState<VideoRoom | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [embed, setEmbed] = useState<string | null>(null);
  const [step, setStep] = useState<SharedStep>('family');
  const [sharing, setSharing] = useState(false);
  const [decisions, setDecisions] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [follow, setFollow] = useState<{ text: string; dueDate: string; by: 'advisor' | 'client' }[]>([]);
  const meetingId = useRef<string | null>(null);
  const known = useRef(new Set<string>());
  const [callNotes, setCallNotes] = useState('');
  const [elsewhere, setElsewhere] = useState<string | null>(null);
  // the latest values for the 3-second poll (it runs with the first render's closure)
  const live = useRef({ notes: '', decisions: [] as string[], callNotes: '', importing: false });
  live.current.notes = notes; live.current.decisions = decisions; live.current.callNotes = callNotes;
  const st = useVideoStatus();
  const account = useAccount();
  const load = async () => {
    if (!api || !v) return;
    try {
      const r = await api.videoRoom(v.roomId);
      setRoom(r); setErr(null);
      for (const g of r.guests.filter((x) => x.status === 'waiting' && !known.current.has(x.id))) { known.current.add(g.id); rt.toast(t('vcall.guest_waiting', { name: g.name }), 'info'); }
      // notes typed in the call window: put on this client's meeting record (the existing path), then the platform drops its copy
      if (p && r.hostNotes && r.hostNotes.rev > r.hostNotes.collectedRev && !live.current.importing) void importCallNotes();
    } catch (e) { setErr(videoErr(rt, e)); }
  };
  useEffect(() => { void load(); const i = setInterval(() => void load(), 3000); return () => clearInterval(i); }, [v?.roomId]);
  // the meeting page shows the advisor's photo, name, title and business contacts from the branding profile
  useEffect(() => { const c = hostCard(rt); const k = JSON.stringify(c); if (api && k !== cardSent) void api.videoCard(c).then(() => { cardSent = k; }).catch(() => undefined); }, [rt.brand]);
  useEffect(() => { const k = (e: KeyboardEvent) => { if (e.key === 'Escape' && !document.querySelector('.overlay')) rt.closeLayer(); }; window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k); }, []);
  const tx = { t: (k: string, pr?: Record<string, string | number>) => t(k, pr), money: (n: number, c?: string) => rt.i18n.money(n, c), enumLabel: (e: string, val: string) => rt.i18n.enumLabel(e as never, val as never), date: (d: string | null | undefined) => (d ? rt.i18n.date(d) : '') };
  const preview: SharedView | null = p ? sharedMeetingView(view, p.id, step, today, tx, { decisions, region: rt.region }) : null;
  // a step or the agreed topics change while the client is watching: the Shared View follows
  useEffect(() => { if (sharing && preview && v) void api?.videoShare(v.roomId, preview).catch(() => undefined); }, [step, decisions.join('|'), sharing]);
  if (!task || !v) return null;
  const act = async (fn: () => Promise<unknown>) => { try { await fn(); await load(); } catch (e) { rt.toast(videoErr(rt, e), 'error'); } };
  const openCall = async (inPanel: boolean) => {
    try {
      const h = await api!.videoHost(v.roomId);
      // a pane that cannot use the camera (an embedded browser): the call belongs in Safari / Chrome
      if (!isDesktopApp() && await cameraBlocked()) { setElsewhere(h.url); return; }
      setElsewhere(null);
      if (inPanel) setEmbed(h.url);
      // the installed app opens the call in the system browser (WebRTC there is the browser's, camera permissions included);
      // the web app opens its own window; a blocked pop-up falls back to this panel
      else if (isDesktopApp()) await openExternal(h.url);
      else if (!window.open(h.url, `avrastro-call-${v.roomId}`, 'popup,width=1180,height=820')) setEmbed(h.url);
    } catch (e) { rt.toast(videoErr(rt, e), 'error'); }
  };
  const share = async (on: boolean) => act(async () => { await api!.videoShare(v.roomId, on ? preview : null); setSharing(on); rt.toast(t(on ? 'vcall.shared_on' : 'vcall.shared_off'), 'success'); });
  const postLink = (kind: 'esign' | 'portal', url: string, text: string) => act(() => api!.videoChat(v.roomId, { kind, url, text }).then(() => rt.toast(t('vcall.chat_posted'), 'success')));
  const site = account?.website ?? null;
  const portalUrl = site ? `${site.replace(/\/+$/, '')}/citizen-dashboard/` : null;
  const allNotes = (own: string, fromCall: string) => [own.trim(), fromCall.trim() ? `${t('vcall.call_notes')}:\n${fromCall.trim()}` : ''].filter(Boolean).join('\n\n');
  async function importCallNotes() {
    if (!p || !api || !v || !task) return;
    live.current.importing = true;
    try {
      const n = await api.videoNotes(v.roomId);
      if (n.rev <= n.collectedRev || n.text == null) return;
      const L = live.current;
      const cs = await rt.run((vw, c) => saveMeeting(vw, { id: meetingId.current ?? undefined, personId: p.id, at: task.dueDate ? localIso(task.dueDate, task.dueTime ?? '09:00') : c.now, purpose: t('vcall.meeting_purpose'), notes: allNotes(L.notes, n.text!), decisions: L.decisions.join('\n'), taskId: task.id }, c));
      if (!cs) return;
      meetingId.current = cs.entityId!;
      setCallNotes(n.text);
      await api.videoNotesAck(v.roomId, n.rev);
    } catch { /* the next poll tries again */ } finally { live.current.importing = false; }
  }
  const saveNotes = async (finish: boolean) => {
    if (!p) return;
    const cs = await rt.run((vw, c) => saveMeeting(vw, { id: meetingId.current ?? undefined, personId: p.id, at: task.dueDate ? localIso(task.dueDate, task.dueTime ?? '09:00') : c.now, purpose: t('vcall.meeting_purpose'), notes: allNotes(notes, callNotes), decisions: decisions.join('\n'), taskId: task.id }, c), finish ? undefined : 'vcall.notes_saved');
    if (cs) meetingId.current = cs.entityId!;
    if (finish && meetingId.current) {
      const ok = await rt.run((vw, c) => completeMeeting(vw, meetingId.current!, { notes: allNotes(notes, callNotes), decisions: decisions.join('\n'), commitments: follow.filter((f) => f.text.trim()).map((f) => ({ by: f.by, text: f.text, dueDate: f.dueDate || null })) }, c), 'vcall.meeting_saved');
      if (ok) setFollow([]);
    }
  };
  const waiting = room?.guests.filter((g) => g.status === 'waiting') ?? [];
  const inCall = room?.guests.filter((g) => g.status === 'admitted') ?? [];
  const usage = st?.usage;
  return (
    <aside className="layer video-panel" role="dialog" aria-modal="false" aria-label={t('vcall.panel_title')} data-testid="video-panel">
      <header className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <div><div className="upper">{t('vcall.panel_title')}</div><b>{p?.displayName ?? task.title}</b><div className="tiny muted">{rt.i18n.date(task.dueDate, 'long')} · {rt.i18n.time(task.dueTime)}{room ? ` · ${t(`vcall.status.${room.status}`)}` : ''}</div></div>
        <button className="btn btn-ghost" onClick={() => rt.closeLayer()} aria-label={t('common.close')}><Icon name="close" /></button>
      </header>
      <div className="stack video-panel-body">
        {err && <div className="callout warn small" role="alert">{err}</div>}
        <p className="tiny muted" style={{ margin: 0 }}>{t('vcall.privacy_note')}</p>
        <div className="row wrap" style={{ gap: 8 }}>
          <button className="btn btn-primary" onClick={() => void openCall(false)} data-testid="video-open-call"><Icon name="camera" />{t('vcall.open_call')}</button>
          <button className="btn" onClick={() => void openCall(true)} data-testid="video-open-embedded">{t('vcall.open_here')}</button>
        </div>
        {elsewhere && <div className="callout warn small" role="alert" data-testid="video-elsewhere">
          <b>{t('vcall.elsewhere_h')}</b>
          <p style={{ margin: '4px 0 8px' }}>{t('vcall.elsewhere_p')}</p>
          <div className="row" style={{ gap: 8 }}><input className="input grow" readOnly value={elsewhere} aria-label={t('vcall.elsewhere_h')} />
            <button className="btn btn-sm" onClick={() => void navigator.clipboard?.writeText(elsewhere).then(() => rt.toast(t('common.copied'), 'success'))}><Icon name="copy" />{t('common.copy')}</button>
            <button className="btn btn-sm" onClick={() => void openExternal(elsewhere).catch(() => undefined)}>{t('vcall.elsewhere_open')}</button></div>
          <div className="tiny muted" style={{ marginTop: 4 }}>{t('vcall.elsewhere_once')}</div>
        </div>}
        {embed && <iframe className="video-embed" title={t('vcall.call_frame')} src={embed} allow="camera; microphone; display-capture; autoplay" data-testid="video-iframe" />}
        <section aria-labelledby="vp-lobby">
          <h3 id="vp-lobby" className="upper">{t('vcall.lobby')}</h3>
          {!waiting.length ? <p className="small muted" data-testid="video-lobby-empty">{t('vcall.lobby_empty')}</p> : (
            <ul className="stack" style={{ listStyle: 'none', padding: 0, gap: 6 }} data-testid="video-lobby">
              {waiting.map((g) => <li key={g.id} className="row" style={{ gap: 8, alignItems: 'center' }}><b className="grow">{g.name}</b>
                <button className="btn btn-sm btn-primary" onClick={() => void act(() => api!.videoAction(v.roomId, 'admit', { guest: g.id }))} data-testid="video-admit">{t('vcall.admit')}</button>
                <button className="btn btn-sm btn-ghost btn-danger" onClick={() => void act(() => api!.videoAction(v.roomId, 'deny', { guest: g.id }))} data-testid="video-deny">{t('vcall.deny')}</button></li>)}
            </ul>)}
          {!!inCall.length && <p className="tiny muted">{t('vcall.in_call', { names: inCall.map((g) => g.name).join(', ') })}</p>}
          <div className="row wrap" style={{ gap: 8 }}>
            <button className="btn btn-sm" aria-pressed={!!room?.locked} onClick={() => void act(() => api!.videoAction(v.roomId, 'lock', { locked: !room?.locked }))} data-testid="video-lock"><Icon name="lock" />{t(room?.locked ? 'vcall.unlock' : 'vcall.lock')}</button>
            <button className="btn btn-sm btn-ghost btn-danger" onClick={() => void act(() => api!.videoAction(v.roomId, 'end'))} data-testid="video-end">{t('vcall.end_all')}</button>
          </div>
        </section>
        {room && <VideoLinkBox room={room} />}
        {room && <button className="btn btn-sm btn-ghost" onClick={() => void act(() => api!.videoAction(v.roomId, 'revoke'))} data-testid="video-revoke">{t('vcall.revoke')}</button>}
        {p && <section aria-labelledby="vp-shared">
          <h3 id="vp-shared" className="upper">{t('vcall.shared_title')}</h3>
          <p className="tiny muted" style={{ margin: 0 }}>{t('vcall.shared_note')}</p>
          <select className="input" value={step} onChange={(e: { target: HTMLSelectElement }) => setStep(e.target.value as SharedStep)} aria-label={t('vcall.shared_step')} data-testid="video-shared-step">
            {SHARED_STEPS.map((s) => <option key={s} value={s}>{t(`meeting_mode.step.${s}`)}</option>)}
          </select>
          <div className="video-shared-preview" aria-label={t('vcall.shared_preview')} data-testid="video-shared-preview">
            <b>{preview?.title}</b>
            {preview?.blocks.map((b, i) => <div key={i} className="small">{b.t === 'kv' ? `${b.label}: ${b.value}` : b.t === 'status' ? `${b.label}: ${b.text}` : b.t === 'list' ? b.items.join(' · ') : b.text}</div>)}
          </div>
          <div className="row wrap" style={{ gap: 8 }}>
            {!sharing ? <button className="btn btn-sm btn-primary" onClick={() => void share(true)} data-testid="video-share-on">{t('vcall.share_on')}</button>
              : <button className="btn btn-sm" onClick={() => void share(false)} data-testid="video-share-off">{t('vcall.share_off')}</button>}
          </div>
        </section>}
        <section aria-labelledby="vp-chat">
          <h3 id="vp-chat" className="upper">{t('vcall.chat_title')}</h3>
          <div className="row wrap" style={{ gap: 8 }}>
            {p && <button className="btn btn-sm" onClick={() => rt.open(<SendForSignature personId={p.id} onSent={(url) => void postLink('esign', url, t('vcall.chat_esign'))} />)} data-testid="video-chat-esign"><Icon name="document" />{t('vcall.chat_esign_btn')}</button>}
            {portalUrl && <button className="btn btn-sm" onClick={() => void postLink('portal', portalUrl, t('vcall.chat_portal'))} data-testid="video-chat-portal"><Icon name="globe" />{t('vcall.chat_portal_btn')}</button>}
          </div>
        </section>
        {p && <section aria-labelledby="vp-notes">
          <h3 id="vp-notes" className="upper">{t('vcall.notes_title')}</h3>
          <div className="row wrap" style={{ gap: 6 }}>{SHARED_TOPICS(rt, p.id).map((label) => { const on = decisions.includes(label); return <button key={label} className={`chip toggle ${on ? 'on' : ''}`} aria-pressed={on} onClick={() => setDecisions(on ? decisions.filter((d) => d !== label) : [...decisions, label])}>{label}</button>; })}</div>
          {callNotes && <div className="callout small" data-testid="video-call-notes"><b>{t('vcall.call_notes')}</b><div style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>{callNotes}</div><div className="tiny muted">{t('vcall.call_notes_saved')}</div></div>}
          <textarea className="textarea" rows={4} value={notes} onChange={(e: { target: HTMLTextAreaElement }) => setNotes(e.target.value)} placeholder={t('vcall.notes_ph')} aria-label={t('vcall.notes_title')} data-testid="video-notes" />
          <p className="tiny muted" style={{ margin: 0 }}>{t('vcall.notes_private')}</p>
          {follow.map((f, i) => <div key={i} className="row" style={{ gap: 6 }}>
            <input className="input grow" value={f.text} onChange={(e: Ev) => setFollow(follow.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))} placeholder={t('vcall.follow_ph')} aria-label={t('vcall.follow_ph')} data-testid={`video-follow-${i}`} />
            <DateField value={f.dueDate} onChange={(d) => setFollow(follow.map((x, j) => (j === i ? { ...x, dueDate: d } : x)))} />
            <select className="input" style={{ width: 'auto' }} value={f.by} onChange={(e: { target: HTMLSelectElement }) => setFollow(follow.map((x, j) => (j === i ? { ...x, by: e.target.value as 'advisor' | 'client' } : x)))} aria-label={t('vcall.follow_by')}><option value="advisor">{t('vcall.by_advisor')}</option><option value="client">{t('vcall.by_client')}</option></select>
          </div>)}
          <div className="row wrap" style={{ gap: 8 }}>
            <button className="btn btn-sm btn-ghost" onClick={() => setFollow([...follow, { text: '', dueDate: addDays(today, 7), by: 'advisor' }])} data-testid="video-follow-add"><Icon name="plus" />{t('vcall.follow_add')}</button>
            <button className="btn btn-sm" onClick={() => void saveNotes(false)} data-testid="video-notes-save">{t('vcall.notes_save')}</button>
            <button className="btn btn-sm btn-primary" onClick={() => void saveNotes(true)} data-testid="video-meeting-finish">{t('vcall.meeting_finish')}</button>
          </div>
        </section>}
        {!!room?.feedback?.length && <section aria-labelledby="vp-feedback" data-testid="video-feedback">
          <h3 id="vp-feedback" className="upper">{t('vcall.feedback_title')}</h3>
          {room.feedback.map((f, i) => <p key={i} className="small" style={{ margin: '2px 0' }}><span aria-label={t('vcall.feedback_stars', { n: f.rating })} style={{ color: 'var(--gold)', letterSpacing: 2 }}>{'★'.repeat(f.rating)}{'☆'.repeat(5 - f.rating)}</span>{f.comment ? ` — «${f.comment}»` : ''}</p>)}
        </section>}
        {usage && <p className={`tiny ${usage.warning ? 'warn' : 'muted'}`} data-testid="video-usage">{t('vcall.usage', { minutes: Math.round(usage.minutes), limit: usage.limit })}{usage.warning ? ` · ${t('vcall.usage_warn')}` : ''}</p>}
        <p className="tiny muted" style={{ margin: 0 }}>{t('vcall.no_recording')}</p>
      </div>
    </aside>
  );
}
/** discussion topics of Meeting Mode, as labels (what the advisor can tick as agreed) */
function SHARED_TOPICS(rt: Runtime, personId: string): string[] {
  const v = sharedMeetingView(rt.view, personId, 'discussion', rt.today, { t: (k, p) => rt.t(k, p), money: (n) => String(n), enumLabel: (_e, x) => x, date: (d) => d ?? '' });
  const list = v.blocks.find((b) => b.t === 'list');
  return list && list.t === 'list' ? list.items : [];
}

// ---------------------------------------------------------------- the call watcher (anywhere in the CRM)
/** a short two-note chime (no file; a blocked AudioContext simply stays silent) */
function chime() {
  try {
    const A = (globalThis as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).AudioContext ?? (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!A) return;
    const ctx = new A();
    ([[880, 0], [1320, 0.18]] as const).forEach(([f, at]) => {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.frequency.value = f; o.connect(g); g.connect(ctx.destination);
      g.gain.setValueAtTime(0.0001, ctx.currentTime + at); g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + at + 0.02); g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + at + 0.45);
      o.start(ctx.currentTime + at); o.stop(ctx.currentTime + at + 0.5);
    });
    setTimeout(() => void ctx.close().catch(() => undefined), 1500);
  } catch { /* no sound */ }
}
/** a notification outside the page: the installed app's own, else the browser's (when the advisor allowed it) */
async function systemNotify(title: string, body: string, onClick: () => void) {
  if (isDesktopApp()) { const { showNative } = await import('../core/local-reminders.ts'); await showNative(title, body); return; }
  try {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    const n = new Notification(title, { body, tag: `avrastro-${title}`, requireInteraction: true } as NotificationOptions);
    n.onclick = () => { window.focus(); onClick(); n.close(); };
  } catch { /* not allowed here */ }
}
/** open the advisor's call window for a room (a blank window first, inside the click, so a pop-up blocker lets it through) */
export async function joinVideoRoom(rt: Runtime, roomId: string, taskId: string | null) {
  const api = accountService()?.api;
  if (!api) return;
  const w = !isDesktopApp() && typeof window !== 'undefined' ? window.open('', `avrastro-call-${roomId}`, 'popup,width=1180,height=820') : null;
  try {
    const h = await api.videoHost(roomId);
    if (isDesktopApp()) await openExternal(h.url);
    else if (w) w.location.href = h.url;
    else if (taskId) { openVideoPanel(rt, taskId); rt.toast(rt.t('vcall.popup_blocked'), 'info'); }
    else window.location.href = h.url;
  } catch (e) { w?.close(); rt.toast(videoErr(rt, e), 'error'); }
}

type Alert = { key: string; kind: 'waiting' | 'soon'; room: VideoRoom; names?: string; minutes?: number };
const SOON_MIN = 10;
/** every 15 s while signed in with the add-on: a client in the waiting room → a card with «Join now», a chime and a system
 *  notification; 10 minutes before a video appointment → a reminder card. Wherever the advisor is in the CRM. */
export function VideoCallWatcher() {
  const rt = useRuntime();
  const st = useVideoStatus();
  const rtRef = useRef(rt); rtRef.current = rt;
  const told = useRef(new Set<string>());
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [perm, setPerm] = useState<string>(typeof Notification === 'undefined' ? 'unsupported' : Notification.permission);
  useEffect(() => {
    if (!st?.entitled) return;
    let live = true;
    const check = async () => {
      const api = accountService()?.api;
      if (!api) return;
      let rooms: VideoRoom[] = [];
      try { rooms = (await api.videoRooms()).items; } catch { return; }
      if (!live) return;
      const r = rtRef.current, now = Date.now();
      const next: Alert[] = [];
      for (const room of rooms) {
        if (room.status === 'ended' || room.status === 'cancelled' || room.status === 'expired') continue;
        const waiting = room.guests.filter((g) => g.status === 'waiting');
        if (waiting.length) {
          const key = `w:${room.id}:${waiting.map((g) => g.id).sort().join(',')}`;
          const names = waiting.map((g) => g.name).join(', ');
          next.push({ key, kind: 'waiting', room, names });
          if (!told.current.has(key)) {
            told.current.add(key); chime();
            void systemNotify(r.t('vcall.alert_waiting_title', { names }), room.title, () => void joinVideoRoom(rtRef.current, room.id, room.taskId));
          }
          continue;
        }
        const mins = Math.ceil((Date.parse(room.startsAt) - now) / 60_000);
        if (mins <= SOON_MIN && mins >= -5 && !room.guests.some((g) => g.status === 'admitted')) {
          const key = `s:${room.id}:${room.startsAt}`;
          next.push({ key, kind: 'soon', room, minutes: Math.max(0, mins) });
          if (!told.current.has(key)) {
            told.current.add(key); chime();
            void systemNotify(r.t('vcall.alert_soon_title', { minutes: Math.max(0, mins) }), room.title, () => void joinVideoRoom(rtRef.current, room.id, room.taskId));
          }
        }
      }
      setAlerts(next);
    };
    void check();
    const i = setInterval(() => void check(), 15_000);
    return () => { live = false; clearInterval(i); };
  }, [st?.entitled]);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const shown = alerts.filter((a) => !hidden.has(a.key));
  // the tab title blinks while a client waits (the advisor may be in another tab)
  useEffect(() => {
    const w = shown.find((a) => a.kind === 'waiting');
    if (!w || typeof document === 'undefined') return;
    const base = document.title; let on = false;
    const i = setInterval(() => { on = !on; document.title = on ? `● ${rt.t('vcall.alert_waiting_tab')}` : base; }, 1000);
    return () => { clearInterval(i); document.title = base; };
  }, [shown.some((a) => a.kind === 'waiting')]);
  if (!shown.length) return null;
  const t = rt.t;
  return (
    <div className="vcall-alerts" role="alert" aria-live="assertive" data-testid="video-alerts">
      {shown.map((a) => (
        <div key={a.key} className={`vcall-alert ${a.kind}`} data-testid={`video-alert-${a.kind}`}>
          <div className="vcall-alert-icon"><Icon name="camera" /></div>
          <div className="vcall-alert-text">
            <b>{a.kind === 'waiting' ? t('vcall.alert_waiting_title', { names: a.names ?? '' }) : t('vcall.alert_soon_title', { minutes: a.minutes ?? 0 })}</b>
            <span>{a.room.title} · {rt.i18n.time(new Date(a.room.startsAt).toTimeString().slice(0, 5))}</span>
          </div>
          <div className="vcall-alert-actions">
            <button className="btn btn-primary" onClick={() => void joinVideoRoom(rt, a.room.id, a.room.taskId)} data-testid="video-alert-join"><Icon name="camera" />{t('vcall.alert_join')}</button>
            {a.room.taskId && <button className="btn btn-sm btn-ghost" onClick={() => openVideoPanel(rt, a.room.taskId!)}>{t('vcall.open_panel')}</button>}
            <button className="btn btn-sm btn-ghost" onClick={() => setHidden(new Set([...hidden, a.key]))} aria-label={t('common.close')}><Icon name="close" /></button>
          </div>
          {perm === 'default' && !isDesktopApp() && <button className="vcall-alert-perm" onClick={() => void Notification.requestPermission().then(setPerm)}>{t('vcall.alert_enable_notifications')}</button>}
        </div>
      ))}
    </div>
  );
}
